import { useQuery } from "@tanstack/react-query";
import { fetchQuestionBankFilterList } from "../services/questionBankFilterService";

function getOptionValue(option = {}, valueKeys = []) {
  for (const key of valueKeys) {
    if (option?.[key] !== undefined && option?.[key] !== null && option?.[key] !== "") {
      return option[key];
    }
  }

  return option?.value || option?.slug || option?.name || option?.label || option?.id || "";
}

function normalizeFilterOptions(options = []) {
  return (Array.isArray(options) ? options : []).map((option, index) => {
    if (typeof option === "string") {
      return {
        id: `${index}-${option}`,
        label: option,
        value: option,
      };
    }

    return {
      id: option?.id || option?.value || option?.slug || `${index}-${option?.label || option?.name || "option"}`,
      label: option?.label || option?.name || option?.title || option?.value || "Option",
      value: option?.value || option?.slug || option?.name || option?.label || "",
    };
  });
}

const FILTER_GROUP_CONFIG = {
  languages: {
    title: "Languages",
    icon: "bi bi-translate",
    valueKeys: ["lang_short_name"],
    labelKey: "name",
  },
  question_level: {
    title: "Question Level",
    icon: "bi bi-bar-chart-line",
    valueKeys: ["data_key"],
    labelKey: "name",
  },
  question_types: {
    title: "Question Type",
    icon: "bi bi-file-earmark-text",
    valueKeys: ["slug"],
    labelKey: "name",
  },
  question_importance: {
    title: "Question Importance",
    icon: "bi bi-star",
    valueKeys: ["data_key"],
    labelKey: "name",
  },
  tags: {
    title: "Tags",
    icon: "bi bi-tags",
    valueKeys: ["tags_category_id"],
    labelKey: "name",
  },
  text_pic_filter: {
    title: "Text / Picture",
    icon: "bi bi-images",
    valueKeys: ["text_pic"],
    labelKey: "name",
  },
  free_paid_filter: {
    title: "Free / Paid",
    icon: "bi bi-currency-rupee",
    valueKeys: ["free_paid", "free_paid_id"],
    labelKey: "name",
  },
  personal_notes: {
    title: "Personal Notes",
    icon: "bi bi-journal-text",
    valueKeys: ["personal_notes_id"],
    labelKey: "name",
  },
  personal_infographics: {
    title: "Personal Infographics",
    icon: "bi bi-journal-text",
    valueKeys: ["personal_infographics_id"],
    labelKey: "name",
  },
  solved_unsolved: {
    title: "Solved / Unsolved",
    icon: "bi bi-check2-square",
    valueKeys: ["solved_unsolved_id"],
    labelKey: "name",
  },
  correct_incorrect: {
    title: "Correct / Incorrect",
    icon: "bi bi-check2-circle",
    valueKeys: ["correct_incorrect_id"],
    labelKey: "name",
  },
  guess_filters: {
    title: "Guess Filters",
    icon: "bi bi-lightbulb",
    valueKeys: ["guess_filter_id"],
    labelKey: "name",
  },
  read_unread: {
    title: "Read / Unread",
    icon: "bi bi-book",
    valueKeys: ["read_unread_id"],
    labelKey: "name",
  },
};

function normalizeFilterGroups(payload = {}) {
  const rawGroups =
    payload?.filter_list ||
    payload?.filters ||
    payload?.filter_groups ||
    payload?.data ||
    payload ||
    [];

  if (Array.isArray(rawGroups)) {
    return rawGroups.map((group, index) => ({
      id: group?.id || group?.slug || group?.key || group?.filter_key || `filter-${index}`,
      title: group?.title || group?.name || group?.filter_name || "Filter",
      defaultValue:
        group?.default_value ||
        group?.selected_value ||
        group?.default_label ||
        "All",
      icon: group?.icon || "bi bi-sliders2",
      options: normalizeFilterOptions(
        group?.options || group?.filter_options || group?.list || group?.values || []
      ),
    }));
  }

  return Object.entries(rawGroups).reduce((groups, [key, value]) => {
    if (!Array.isArray(value) || !value.length) {
      return groups;
    }

    const config = FILTER_GROUP_CONFIG[key] || {};
    const options = value.map((option, index) => ({
      id: option?.id || getOptionValue(option, config.valueKeys || []) || `${key}-${index}`,
      label:
        option?.[config.labelKey] ||
        option?.name ||
        option?.title ||
        option?.label ||
        "Option",
      value: getOptionValue(option, config.valueKeys || []),
    }));

    groups.push({
      id: key,
      title: config.title || key.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase()),
      defaultValue: "All",
      icon: config.icon || "bi bi-sliders2",
      options,
    });

    return groups;
  }, []);
}

export function useQuestionBankFilterList(params = {}, options = {}) {
  return useQuery({
    queryKey: ["questionBankFilterList", params],
    queryFn: async () => {
      const { payload } = await fetchQuestionBankFilterList(params);

      return {
        groups: normalizeFilterGroups(payload),
      };
    },
    enabled: options.enabled ?? true,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 2,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    placeholderData: (previousData) =>
      previousData ?? {
        groups: [],
      },
  });
}
